import { Component, Input, Output, EventEmitter, ElementRef, ViewChild, AfterViewInit, ViewChildren, QueryList, OnChanges, signal, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { FlowObjectDefinition, FlowObjectType, FlowObjectTypeClassName } from '../../../models/flow-object.model';
import { Grouping, GroupingType, GroupingTypeClassName } from '../../../models/grouping.model';
import { CdkDragStart } from '@angular/cdk/drag-drop';
import { GroupingColumn } from '../../../models/grouping-column.model';
import { IMPORTS } from './_imports';

@Component({
    selector: 'selected-grouping',
    templateUrl: './selected-grouping.component.html',
    styleUrl: './selected-grouping.component.scss',
    standalone: true,
    imports: IMPORTS
})
export class SelectedGroupingComponent implements OnChanges, OnDestroy, AfterViewInit {
    // #region [ViewChild]
    @ViewChild('contextModeRef') contextModeRef: ElementRef<HTMLElement>;
    @ViewChild('flowObjectBodyContainerRef') flowObjectBodyContainerRef: ElementRef<HTMLElement>;
    @ViewChildren('selectedFlowObjectRef') selectedFlowObjectRef: QueryList<ElementRef>;
    // #endregion

    // #region [Type props]
    FlowObjectType = FlowObjectType;
    FlowObjectTypeClassName = FlowObjectTypeClassName;
    GroupingTypeClassName = GroupingTypeClassName;
    // #endregion

    // #region [props]
    overflowCache = {};
    // #endregion

    // #region [signals]
    $model = signal(null as Grouping);
    $selectedGroupingColumns = signal(null as GroupingColumn[]);
    $selectedFlowObjects = signal(null as FlowObjectDefinition[]);
    // #endregion

    // #region [Input/Output]
    @Input() _model: Grouping;
    @Input() _isReadOnlyMode: boolean;
    @Input() _hasGatewayPath: boolean;
    @Input() _selectedGroupings: Grouping[];
    @Input() _selectedGroupingColumns: GroupingColumn[];
    @Input() _selectedFlowObjects: FlowObjectDefinition[];
    @Output() _toggleEditAreaEvent = new EventEmitter<FlowObjectDefinition>();
    @Output() _removeGroupingEvent = new EventEmitter<{ event: MouseEvent, flowObject: FlowObjectDefinition }>();
    @Output() _changeTaskTypeEvent = new EventEmitter<{ event: MouseEvent, flowObject: FlowObjectDefinition }>();
    @Output() _moveGroupingBackwardsEvent = new EventEmitter<{ event: MouseEvent, flowObject: FlowObjectDefinition }>();
    @Output() _moveGroupingForwardsEvent = new EventEmitter<{ event: MouseEvent, flowObject: FlowObjectDefinition }>();
    @Output() _moveGroupingUpwardsEvent = new EventEmitter<{ event: MouseEvent, flowObject: FlowObjectDefinition }>();
    @Output() _moveGroupingDownwardsEvent = new EventEmitter<{ event: MouseEvent, flowObject: FlowObjectDefinition }>();
    @Output() _notifyFlowObjectLoadedEvent = new EventEmitter<ElementRef[]>();
    // #endregion

    constructor(
        public element: ElementRef,
        private cd: ChangeDetectorRef
    ) { }

    ngOnChanges() {
        this.$model.set(this._model);
        this.$selectedGroupingColumns.set(this._selectedGroupingColumns);
        this.$selectedFlowObjects.set(this._selectedFlowObjects);
    }

    ngAfterViewInit() {
        this.notifyFlowObjectLoaded();
    }

    ngOnDestroy() {
        this.overflowCache = {};
    }

    isOverflown(elem: HTMLElement, id: string): boolean {
        const currentStatus = this.isCurrentlyOverflown(elem);
        if (this.overflowCache[id] != currentStatus) {
            Promise.resolve().then(() => {
                this.overflowCache[id] = currentStatus;
                this.cd.markForCheck();
            });
        }

        return this.overflowCache[id] ?? false;
    }

    canShowEditButton(flowObject: FlowObjectDefinition): boolean {
        return !this._isReadOnlyMode && flowObject.typeId != FlowObjectType.Dummy;
    }

    canShowRemoveButton(flowObject: FlowObjectDefinition): boolean {
        return !this._isReadOnlyMode
            && flowObject.groupingColumn.innerIndex == 0
            && flowObject.typeId != FlowObjectType.Dummy;
    }

    canShowChangeButton(flowObject: FlowObjectDefinition): boolean {
        return !this._isReadOnlyMode
            && this.$model().typeId == GroupingType.Gateway
            && flowObject.innerIndex == 0
            && flowObject.groupingColumn.innerIndex == 1
            && flowObject.typeId != FlowObjectType.Dummy;
    }

    canShowHorizontalMoveButton(flowObject: FlowObjectDefinition): boolean {
        return !this._isReadOnlyMode
            && flowObject.groupingColumn.innerIndex == 0
            && flowObject.typeId != FlowObjectType.GatewayPaths;
    }

    canShowMoveForwardsButton(flowObject: FlowObjectDefinition): boolean {
        let outerIndex = flowObject.groupingColumn.grouping.outerIndex;
        let mainPathLastIndex = this._selectedGroupings.filter(x => !x.isFirstGatewayPath && !x.isSecondGatewayPath).length - 1
            - (this._hasGatewayPath ? 1 : 0);
        let firstGatewayPathLastIndex = this._selectedGroupings.filter(x => !x.isFirstGatewayPath && !x.isSecondGatewayPath).length
            + this._selectedGroupings.filter(x => x.isFirstGatewayPath).length - 1;
        let secondGatewayPathLastIndex = firstGatewayPathLastIndex + this._selectedGroupings.filter(x => x.isSecondGatewayPath).length;

        return (
                outerIndex != mainPathLastIndex
                && !this._hasGatewayPath
            ) || (
                outerIndex != mainPathLastIndex
                && this._hasGatewayPath
                && outerIndex != firstGatewayPathLastIndex
                && outerIndex != secondGatewayPathLastIndex
            );
    }

    canShowMoveBackwardsButton(flowObject: FlowObjectDefinition): boolean {
        let outerIndex = flowObject.groupingColumn.grouping.outerIndex;
        let firstGatewayPathFirstIndex = this._selectedGroupings.filter(x => !x.isFirstGatewayPath && !x.isSecondGatewayPath).length;
        let secondGatewayPathFirstIndex = firstGatewayPathFirstIndex + this._selectedGroupings.filter(x => x.isFirstGatewayPath).length;

        return (
                outerIndex != 0
                && !this._hasGatewayPath
            ) || (
                outerIndex != 0
                && this._hasGatewayPath
                && outerIndex != firstGatewayPathFirstIndex
                && outerIndex != secondGatewayPathFirstIndex
            );
    }

    canShowVerticalMoveButton(flowObject: FlowObjectDefinition): boolean {
        return !this._isReadOnlyMode
            && flowObject.groupingColumn.innerIndex == 0
            && flowObject.typeId != FlowObjectType.GatewayPaths
            && flowObject.typeId != FlowObjectType.Dummy
            && this._hasGatewayPath;
    }

    canShowMoveUpwardsButton(flowObject: FlowObjectDefinition): boolean {
        return flowObject.isFirstGatewayPath
            || flowObject.isSecondGatewayPath;
    }

    canShowMoveDownwardsButton(flowObject: FlowObjectDefinition): boolean {
        return !flowObject.isSecondGatewayPath;
    }

    toggleEditArea(flowObject: FlowObjectDefinition) {
        if (flowObject.typeId == FlowObjectType.Dummy) return;

        this._toggleEditAreaEvent.emit(flowObject);
    }

    removeGrouping(event: MouseEvent, flowObject: FlowObjectDefinition) {
        this._removeGroupingEvent.emit({ event, flowObject });
    }

    changeTaskType(event: MouseEvent, flowObject: FlowObjectDefinition) {
        this._changeTaskTypeEvent.emit({ event, flowObject });
    }

    moveGroupingBackwards(event: MouseEvent, flowObject: FlowObjectDefinition) {
        this._moveGroupingBackwardsEvent.emit({ event, flowObject });
    }

    moveGroupingForwards(event: MouseEvent, flowObject: FlowObjectDefinition) {
        this._moveGroupingForwardsEvent.emit({ event, flowObject });
    }

    moveGroupingUpwards(event: MouseEvent, flowObject: FlowObjectDefinition) {
        this._moveGroupingUpwardsEvent.emit({ event, flowObject });
    }

    moveGroupingDownwards(event: MouseEvent, flowObject: FlowObjectDefinition) {
        this._moveGroupingDownwardsEvent.emit({ event, flowObject });
    }

    notifyFlowObjectLoaded() {
        this._notifyFlowObjectLoadedEvent.emit(this.selectedFlowObjectRef.toArray());
    }

    // #region [===== private methods =====]
    // #endregion

    private isCurrentlyOverflown(elem: HTMLElement) {
        let flowObjectBodyContainer = this.flowObjectBodyContainerRef?.nativeElement;
        if (flowObjectBodyContainer == null) return false;

        // compensação por um fator de ajuste empírico
        return flowObjectBodyContainer.scrollWidth > elem.clientWidth * .87;
    };
}
